import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Button, ConfirmModal, Dropdown, Input, Table } from '../../../../../common/ui';
import { showAlert } from '../../../../../common/actions';
import {
  cancelSupplierReturn,
  loadReturnSources,
  postSupplierReturn,
  previewSupplierReturn,
  saveSupplierReturn,
  searchReturnProducts,
} from '../../actions';
import styles from '../styles.module.css';
import { formatMoney, formatReturnDate, startOfDayMs, statusClassName } from '../format';
import AddLinePopup from '../AddLinePopup';

const REASONS = [
  'Damaged',
  'Expired',
  'Wrong product',
  'Excess quantity',
  'Quality issue',
  'Other',
];

function toPayload(line) {
  const payload = {
    productId: line.productId,
    qty: line.qty,
    netUnitCost: line.netUnitCost,
  };
  if (line.sourceType) {
    payload.sourceType = line.sourceType;
    payload.sourceId = line.sourceId;
    if (line.sourceLineId) payload.sourceLineId = line.sourceLineId;
  }
  return payload;
}

const DraftReturn = ({
  document,
  saveSupplierReturn,
  cancelSupplierReturn,
  previewSupplierReturn,
  postSupplierReturn,
  loadReturnSources,
  searchReturnProducts,
  showAlert,
}) => {
  const navigate = useNavigate();
  const [reason, setReason] = useState(document.reason || '');
  const [dateText, setDateText] = useState(formatReturnDate(document.returnDate));
  const [returnDateMs, setReturnDateMs] = useState(document.returnDate);
  const [lines, setLines] = useState(Array.isArray(document.lines) ? document.lines : []);
  const [sentences, setSentences] = useState([]);
  const [previewKey, setPreviewKey] = useState('');
  const [popupOpen, setPopupOpen] = useState(false);
  const [confirmPost, setConfirmPost] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const linesRef = useRef(lines);
  linesRef.current = lines;

  useEffect(() => {
    setReason(document.reason || '');
    setDateText(formatReturnDate(document.returnDate));
    setReturnDateMs(document.returnDate);
    setLines(Array.isArray(document.lines) ? document.lines : []);
    setSentences([]);
    setPreviewKey('');
  }, [document.id]);

  const lineKey = JSON.stringify(lines.map(toPayload));
  const visibleSentences = previewKey === lineKey ? sentences : [];

  useEffect(() => {
    if (!lines.length) {
      setSentences([]);
      setPreviewKey('');
      return undefined;
    }
    let cancelled = false;
    const requestedKey = lineKey;
    (async () => {
      const result = await previewSupplierReturn(document.id, lines.map(toPayload));
      if (cancelled) return;
      if (result && result.previewFailed) {
        if (result.message) {
          showAlert({
            isAlertOpen: true,
            alertType: 'danger',
            alertMessage: result.message,
          });
        }
        setSentences([]);
        setPreviewKey(requestedKey);
        return;
      }
      setSentences(result && Array.isArray(result.sentences) ? result.sentences : []);
      setPreviewKey(requestedKey);
    })();
    return () => {
      cancelled = true;
    };
  }, [document.id, lineKey, lines, previewSupplierReturn, showAlert]);

  const body = {
    reason,
    returnDate: returnDateMs,
    lines: lines.map(toPayload),
  };

  const columns = [
    { id: 'productName', label: 'Product', minWidth: 160 },
    { id: 'qty', label: 'Quantity', minWidth: 100 },
    {
      id: 'netUnitCost',
      label: 'Net Unit Cost',
      minWidth: 130,
      format: (value) => formatMoney(value),
    },
    {
      id: 'returnValue',
      label: 'Return Value',
      minWidth: 130,
      format: (value) => formatMoney(value),
    },
    {
      id: 'remove',
      label: '',
      minWidth: 90,
      render: (_value, row) => (
        <Button
          variant="secondary"
          onClick={() => setLines((current) => current.filter((line) => line.id !== row.id))}
        >
          Remove
        </Button>
      ),
    },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1 className={styles.pageTitle}>{document.returnNo || 'Supplier return'}</h1>
          <p className={styles.breadcrumb}>Inventory / Suppliers / Return</p>
        </div>
      </div>
      <div className={styles.body}>
        <div className={styles.card}>
          <div className={styles.headerGrid}>
            <div>
              <p className={styles.fieldLabel}>Return No.</p>
              <p className={styles.fieldValue}>{document.returnNo}</p>
            </div>
            <div>
              <Input
                label="Date"
                type="date"
                value={dateText}
                onChange={(value) => {
                  if (!value) {
                    const today = formatReturnDate(Date.now());
                    setDateText(today);
                    setReturnDateMs(startOfDayMs(today));
                    return;
                  }
                  setDateText(value);
                  setReturnDateMs(startOfDayMs(value));
                }}
              />
            </div>
            <div>
              <p className={styles.fieldLabel}>Supplier</p>
              <p className={styles.fieldValue}>{document.supplierName}</p>
            </div>
            <div>
              <Dropdown
                label="Reason"
                placeholder="Reason"
                options={REASONS.map((item) => ({ value: item, label: item }))}
                value={reason}
                onChange={setReason}
              />
            </div>
            <div>
              <p className={styles.fieldLabel}>Status</p>
              <p className={styles.fieldValue}>
                <span className={statusClassName(styles, document.status)}>{document.status}</span>
              </p>
            </div>
          </div>
          <div className={styles.actionRow}>
            <Button onClick={() => setPopupOpen(true)}>Add line</Button>
          </div>
          <Table columns={columns} rows={lines} emptyMessage="" />
          <div className={styles.preview}>
            {visibleSentences.map((sentence, index) => (
              <p key={`${index}-${sentence}`} className={styles.previewSentence}>{sentence}</p>
            ))}
          </div>
          <div className={styles.actionRow}>
            <Button
              onClick={async () => {
                const requestedKey = JSON.stringify(linesRef.current.map(toPayload));
                const saved = await saveSupplierReturn(document.id, body);
                const currentKey = JSON.stringify((linesRef.current || []).map(toPayload));
                if (requestedKey !== currentKey) return;
                if (saved && Array.isArray(saved.lines)) setLines(saved.lines);
              }}
            >
              Save Draft
            </Button>
            <Button variant="secondary" onClick={() => setConfirmCancel(true)}>Cancel Draft</Button>
            <Button
              onClick={() => {
                if (!lines.length) {
                  showAlert({
                    isAlertOpen: true,
                    alertType: 'danger',
                    alertMessage: 'Add a line before posting.',
                  });
                  return;
                }
                setConfirmPost(true);
              }}
            >
              Post
            </Button>
          </div>
          <button type="button" className={styles.backLink} onClick={() => navigate(-1)}>
            Back
          </button>
        </div>
      </div>
      {popupOpen && (
        <AddLinePopup
          supplierId={document.supplierId}
          existingLines={lines}
          onClose={() => setPopupOpen(false)}
          onAdd={(line) => setLines((current) => [...current, line])}
          loadReturnSources={loadReturnSources}
          searchReturnProducts={searchReturnProducts}
        />
      )}
      {confirmPost && !popupOpen && (
        <ConfirmModal
          open
          title="Post"
          message={visibleSentences.join(' ')}
          confirmLabel="Post"
          cancelLabel="Cancel"
          onClose={() => setConfirmPost(false)}
          onConfirm={() => {
            postSupplierReturn(document.id, body);
          }}
        />
      )}
      {confirmCancel && !popupOpen && !confirmPost && (
        <ConfirmModal
          open
          title="Cancel Draft"
          message="This draft will be removed. Fridge and We owe stay the same."
          confirmLabel="Cancel Draft"
          cancelLabel="Keep Draft"
          onClose={() => setConfirmCancel(false)}
          onConfirm={() => {
            cancelSupplierReturn(document.id).then((ok) => {
              if (ok) navigate(`/inventory/suppliers/view/${document.supplierId}`);
            });
          }}
        />
      )}
    </div>
  );
};

DraftReturn.propTypes = {
  document: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    returnNo: PropTypes.string,
    supplierId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    supplierName: PropTypes.string,
    status: PropTypes.string,
    reason: PropTypes.string,
    returnDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    lines: PropTypes.array,
  }).isRequired,
  saveSupplierReturn: PropTypes.func.isRequired,
  cancelSupplierReturn: PropTypes.func.isRequired,
  previewSupplierReturn: PropTypes.func.isRequired,
  postSupplierReturn: PropTypes.func.isRequired,
  loadReturnSources: PropTypes.func.isRequired,
  searchReturnProducts: PropTypes.func.isRequired,
  showAlert: PropTypes.func.isRequired,
};

export default connect(null, {
  saveSupplierReturn,
  cancelSupplierReturn,
  previewSupplierReturn,
  postSupplierReturn,
  loadReturnSources,
  searchReturnProducts,
  showAlert,
})(DraftReturn);
